import { API_User } from "../models/models_generated";
import { Observable } from "../utils/observable";
import { isMobile } from "../utils/browser";
import { API_SERVICE } from "./APIService";

class ApplicationService {

	initialized = new Observable<boolean>(false);

	// Cache for user data
	private currentUser: API_User | null = null;
	private userLoadedAt: number = 0;
	private readonly USER_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

	// Local storage keys
	private readonly GOBAN_WIDTH_KEY = 'gobanWidth';

	// Get goban width from localStorage (with mobile/desktop default)
	getGobanWidth(): number {
		const stored = localStorage.getItem(this.GOBAN_WIDTH_KEY);
		if (stored !== null) {
			return parseInt(stored, 10);
		}
		// Default: 100 on mobile, 66 on desktop
		return isMobile() ? 100 : 66;
	}

	// Set goban width in localStorage
	setGobanWidth(width: number): void {
		localStorage.setItem(this.GOBAN_WIDTH_KEY, String(width));
	}

	constructor() {
		setTimeout(async () => {
			try {
				await API_SERVICE.checkLoggedIn();
				if (API_SERVICE.loggedIn.get()) {
					// Load user data (includes settings)
					await this.loadUser();
				}
			} catch (e) {
				console.error("Error initializing", e);
			}
			this.initialized.set(true);
		}, 1);
	}

	// Load current user (with settings)
	async loadUser(force: boolean = false): Promise<API_User> {
		const now = Date.now();
		const cacheExpired = this.userLoadedAt === 0 || (now - this.userLoadedAt) > this.USER_CACHE_DURATION;

		if (force || !this.currentUser || cacheExpired) {
			this.currentUser = await API_SERVICE.getMe();
			this.userLoadedAt = now;
		}
		return this.currentUser;
	}

	// Get cached user
	getCurrentUser(): API_User | null {
		return this.currentUser;
	}

	// Update settings
	async updateSettings(settings: { maxTriesPerMove: number; maxTimePerMove: number; sound: boolean }): Promise<void> {
		await API_SERVICE.updateSettings(settings);
		// Refresh cached user
		this.currentUser = await API_SERVICE.getMe();
		this.userLoadedAt = Date.now();
	}

	// Logout user
	async logout(): Promise<void> {
		await API_SERVICE.logout();
		this.clearCache();
	}

	// Clear cache (e.g., on logout)
	clearCache() {
		this.currentUser = null;
		this.userLoadedAt = 0;
	}
}

export const APPLICATION_SERVICE = new ApplicationService();
